import { logError } from '@databases/logs';
import { defaultStoreUtils } from '@databases/redux';
import {
  DatabaseLayoutType,
  type DatabaseView,
  databaseViewSettingsValidator,
  type DatabaseViewSettings,
  DEFAULT_DATABASE_VIEW_SETTINGS,
  ViewId,
} from '@databases/types/database/view';
import { getCustomProperty } from '@databases/utils/confluence/utils';
import { isBrokenDatabase } from '@databases/utils/database/utils';
import {
  applyViewChange,
  getDefaultView,
  getViewById,
  type ViewChangeType,
} from '@databases/utils/database/view/view';
import { selectDatabaseByContentIdOptional } from '@databases/utils/redux/databases/selectors';
import { useEffect } from 'react';

export interface LayoutSettingsInterface {
  [DatabaseLayoutType.TABLE]: DatabaseViewSettings['layout'];
  [DatabaseLayoutType.GALLERY]: DatabaseViewSettings['layout'];
  [DatabaseLayoutType.BOARD]: DatabaseViewSettings['layout'];
  [DatabaseLayoutType.CALENDAR]: DatabaseViewSettings['layout'];
}

export const initialLayoutSettings: LayoutSettingsInterface = {
  [DatabaseLayoutType.TABLE]: {
    ...DEFAULT_DATABASE_VIEW_SETTINGS.layout,
    type: DatabaseLayoutType.TABLE,
  },
  [DatabaseLayoutType.GALLERY]: {
    ...DEFAULT_DATABASE_VIEW_SETTINGS.layout,
    type: DatabaseLayoutType.GALLERY,
  },
  [DatabaseLayoutType.BOARD]: {
    ...DEFAULT_DATABASE_VIEW_SETTINGS.layout,
    type: DatabaseLayoutType.BOARD,
  },
  [DatabaseLayoutType.CALENDAR]: {
    ...DEFAULT_DATABASE_VIEW_SETTINGS.layout,
    type: DatabaseLayoutType.CALENDAR,
  },
};

export const ViewLoader = ({
  contentId,
  setLayoutSettings,
  setResolvedView,
  setUnsavedView,
  setView,
  unsavedView,
  unsavedViewId,
  urlSavedViewId,
  viewChangeList,
  viewId,
  onUnsavedViewLoaded,
}: {
  contentId: string;
  setLayoutSettings: (
    layoutSettings: React.SetStateAction<Record<string, LayoutSettingsInterface>>
  ) => void;
  setResolvedView: (view: DatabaseView) => void;
  setUnsavedView: (view: DatabaseView | null) => void;
  setView: (view: DatabaseView) => void;
  unsavedView: DatabaseView | null;
  unsavedViewId: string | null;
  urlSavedViewId: string | null;
  viewChangeList: ViewChangeType[];
  viewId: string | null;
  onUnsavedViewLoaded?: () => void;
}) => {
  const [selectedView, savedView, databaseDefaultView] =
    defaultStoreUtils.useEqualSelector((state) => {
      const database = selectDatabaseByContentIdOptional(state, {
        contentId: contentId ?? '',
      });

      if (!database || isBrokenDatabase(database)) {
        return [undefined, undefined, undefined];
      }

      return [
        getViewById(database.views, viewId),
        getViewById(database.views, urlSavedViewId),
        getDefaultView(database),
      ];
    });

  // Loading unsaved view
  useEffect(() => {
    if (unsavedViewId) {
      getCustomProperty(contentId ?? '', unsavedViewId).then((unsavedView) => {
        if (!unsavedView) {
          return;
        }

        const validatedViewSetting = databaseViewSettingsValidator.safeParse(
          unsavedView.settings
        );

        if (validatedViewSetting.success) {
          setUnsavedView({
            id: ViewId.UNSAVED,
            name: 'Unsaved View',
            settings: validatedViewSetting.data,
          });
          onUnsavedViewLoaded?.();
        } else {
          // Sentry error reference: CTD-4566
          logError(
            new Error(
              `Zod validation failed for unsaved view settings: ${unsavedView.settings}`
            )
          );
        }
      });
    }
  }, [contentId, onUnsavedViewLoaded, setUnsavedView, unsavedViewId]);

  useEffect(() => {
    let baseView = selectedView ?? databaseDefaultView;

    if (unsavedView && viewId === ViewId.UNSAVED) {
      baseView = unsavedView;
    }

    if (baseView) {
      if (viewChangeList.length === 0) {
        setLayoutSettings((prev) => {
          return {
            ...prev,
            [baseView.id]: {
              ...initialLayoutSettings,
              [baseView.settings.layout.type]: baseView.settings.layout,
            },
          };
        });
        setView(baseView);
      } else {
        const newView = viewChangeList.reduce(
          (view, viewChange) => applyViewChange(view, viewChange),
          baseView
        );

        setView(newView);
      }
    }
  }, [
    databaseDefaultView,
    selectedView,
    setLayoutSettings,
    setView,
    unsavedView,
    unsavedViewId,
    viewId,
    viewChangeList,
  ]);

  useEffect(() => {
    if (unsavedView) {
      return setResolvedView(unsavedView);
    } else if (savedView) {
      return setResolvedView(savedView);
    } else if (databaseDefaultView) {
      return setResolvedView(databaseDefaultView);
    }
  }, [databaseDefaultView, savedView, urlSavedViewId, setResolvedView, unsavedView]);

  return null;
};
